import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import axios from 'axios';
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer } from 'recharts';
import { ArrowLeftIcon, CalendarIcon, CreditCardIcon } from '@heroicons/react/24/outline';
// Removed sample data imports - using only real database data

interface PaymentHistoryItem {
  id: string;
  amount: number;
  date: string;
  status: 'PAID' | 'PENDING' | 'OVERDUE';
  type: 'MONTHLY_DUES' | 'SPECIAL_ASSESSMENT' | 'MAINTENANCE_FEE';
  description: string;
  invoiceDate?: string;
  paymentDate?: string;
}

const PaymentHistory: React.FC = () => {
  const { month } = useParams<{ month: string }>();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [payments, setPayments] = useState<PaymentHistoryItem[]>([]);
  const [chartData, setChartData] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [selectedYear] = useState(new Date().getFullYear());

  useEffect(() => {
    fetchPaymentHistory();
  }, [month, user]); // eslint-disable-line react-hooks/exhaustive-deps

  const fetchPaymentHistory = async () => {
    setLoading(true);
    try {
      if (!user?.hoaId || !month) return;

      const response = await axios.get<PaymentHistoryItem[]>('/api/payments', {
        params: { 
          hoaId: user.hoaId,
          year: selectedYear,
          month: month 
        },
        headers: { Authorization: `Bearer ${user?.token}` }
      });

      const paymentData = response.data;
      setPayments(paymentData);

      // Process data for timeline chart showing invoice vs payment dates
      const timelineData = processTimelineData(paymentData);
      setChartData(timelineData);

    } catch (error) {
      console.error('Error fetching payment history:', error);
      // No fallback data - show empty state
      setPayments([]);
      setChartData([]);
    } finally {
      setLoading(false);
    }
  };

  // Removed sample data generation - using only real database data

  const processTimelineData = (paymentData: PaymentHistoryItem[]) => {
    const timelineMap = new Map();

    paymentData.forEach(payment => {
      const day = new Date(payment.date).getDate();
      const key = day;

      if (!timelineMap.has(key)) {
        timelineMap.set(key, {
          day: key,
          invoiced: 0,
          paid: 0,
          date: payment.date
        });
      }

      const entry = timelineMap.get(key);
      entry.invoiced += payment.amount;
      
      if (payment.status === 'PAID' && payment.paymentDate) {
        entry.paid += payment.amount;
      }
    });

    return Array.from(timelineMap.values()).sort((a, b) => a.day - b.day);
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric'
    });
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'PAID': return 'text-green-400 bg-green-500/20';
      case 'PENDING': return 'text-yellow-400 bg-yellow-500/20';
      case 'OVERDUE': return 'text-red-400 bg-red-500/20';
      default: return 'text-white/60 bg-white/10';
    }
  };

  const totalInvoiced = payments.reduce((sum, p) => sum + p.amount, 0);
  const totalPaid = payments.filter(p => p.status === 'PAID').reduce((sum, p) => sum + p.amount, 0);
  const totalPending = payments.filter(p => p.status !== 'PAID').reduce((sum, p) => sum + p.amount, 0);

  return (
    <div className="space-y-8">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center space-x-4">
          <button
            onClick={() => navigate('/payments')}
            className="flex items-center space-x-2 text-white/60 hover:text-white transition-colors"
          >
            <ArrowLeftIcon className="w-5 h-5" />
            <span>Back to Payments</span>
          </button>
          <div className="h-6 w-px bg-white/20"></div>
          <h1 className="text-3xl font-bold text-white">Payment History - {month} {selectedYear}</h1>
        </div>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        <div className="floating-card">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-white/60 text-sm font-medium">Total Invoiced</p>
              <p className="text-3xl font-bold text-[#22d3ee] mt-1">${totalInvoiced.toFixed(2)}</p>
              <p className="text-xs text-white/40 mt-1">{payments.length} invoices</p>
            </div>
            <div className="w-12 h-12 rounded-xl bg-gradient-to-r from-[#22d3ee]/20 to-[#6366f1]/20 flex items-center justify-center">
              <CalendarIcon className="w-6 h-6 text-[#22d3ee]" />
            </div>
          </div>
        </div>

        <div className="floating-card">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-white/60 text-sm font-medium">Total Paid</p>
              <p className="text-3xl font-bold text-green-400 mt-1">${totalPaid.toFixed(2)}</p>
              <p className="text-xs text-white/40 mt-1">{payments.filter(p => p.status === 'PAID').length} payments</p>
            </div>
            <div className="w-12 h-12 rounded-xl bg-gradient-to-r from-green-500/20 to-emerald-500/20 flex items-center justify-center">
              <CreditCardIcon className="w-6 h-6 text-green-400" />
            </div>
          </div>
        </div>

        <div className="floating-card">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-white/60 text-sm font-medium">Outstanding</p>
              <p className="text-3xl font-bold text-red-400 mt-1">${totalPending.toFixed(2)}</p>
              <p className="text-xs text-white/40 mt-1">{payments.filter(p => p.status !== 'PAID').length} pending</p>
            </div>
            <div className="w-12 h-12 rounded-xl bg-gradient-to-r from-red-500/20 to-orange-500/20 flex items-center justify-center">
              <CreditCardIcon className="w-6 h-6 text-red-400" />
            </div>
          </div>
        </div>
      </div>

      {/* Timeline Chart */}
      <div className="floating-card">
        <div className="flex justify-between items-center mb-6">
          <h2 className="text-xl font-semibold text-white">Daily Invoice vs Payment Timeline</h2>
          <div className="text-xs text-white/40 bg-blue-500/10 px-2 py-1 rounded-lg">
            {month} {selectedYear}
          </div>
        </div>
        
        <div className="bg-white/5 backdrop-blur-xl rounded-xl p-4">
          <ResponsiveContainer width="100%" height={300}>
            <BarChart data={chartData}>
              <XAxis 
                dataKey="day" 
                tick={{ fill: '#ffffff80' }}
                tickFormatter={(value) => `${value}`}
              />
              <YAxis tick={{ fill: '#ffffff80' }} />
              <Tooltip 
                contentStyle={{ 
                  backgroundColor: 'rgba(0,0,0,0.8)', 
                  border: '1px solid rgba(255,255,255,0.1)',
                  borderRadius: '12px',
                  color: 'white'
                }}
                labelFormatter={(value) => `Day ${value}`}
              />
              <Bar dataKey="invoiced" fill="url(#gradient1)" radius={[4, 4, 0, 0]} name="Invoiced" />
              <Bar dataKey="paid" fill="url(#gradient2)" radius={[4, 4, 0, 0]} name="Paid" />
              <defs>
                <linearGradient id="gradient1" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="0%" stopColor="#22d3ee" />
                  <stop offset="100%" stopColor="#6366f1" />
                </linearGradient>
                <linearGradient id="gradient2" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="0%" stopColor="#38bdf8" />
                  <stop offset="100%" stopColor="#10b981" />
                </linearGradient>
              </defs>
            </BarChart>
          </ResponsiveContainer>
        </div>
      </div>

      {/* Payment Details Table */}
      <div className="floating-card">
        <div className="flex justify-between items-center mb-6">
          <h2 className="text-xl font-semibold text-white">Payment Details</h2>
          <div className="flex items-center space-x-2">
            <span className="text-xs text-white/60">{payments.length} total records</span>
          </div>
        </div>

        {loading ? (
          <div className="flex justify-center py-12">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#22d3ee]"></div>
          </div>
        ) : (
          <div className="space-y-4">
            {payments.map((payment) => (
              <div key={payment.id} className="flex items-center justify-between p-4 rounded-xl bg-white/5 hover:bg-white/10 transition-colors duration-200">
                <div className="flex-1">
                  <div className="flex items-center space-x-3 mb-2">
                    <h3 className="font-medium text-white">{payment.description}</h3>
                    <span className={`px-2 py-1 rounded-lg text-xs font-medium ${getStatusColor(payment.status)}`}>
                      {payment.status}
                    </span>
                  </div>
                  <div className="flex items-center space-x-6 text-sm text-white/60">
                    <div className="flex items-center space-x-1">
                      <CalendarIcon className="w-4 h-4" />
                      <span>Invoiced: {formatDate(payment.invoiceDate || payment.date)}</span>
                    </div>
                    {payment.paymentDate && (
                      <div className="flex items-center space-x-1">
                        <CreditCardIcon className="w-4 h-4" />
                        <span>Paid: {formatDate(payment.paymentDate)}</span>
                      </div>
                    )}
                  </div>
                </div>
                <div className="text-right">
                  <div className="text-xl font-semibold text-white">${payment.amount.toFixed(2)}</div>
                  <div className="text-xs text-white/40 capitalize">{payment.type.replace(/_/g, ' ').toLowerCase()}</div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default PaymentHistory; 